/**
 * @fileoverview This file is to handle the different tabs in configuration
 *
 * @version 1.0.0
 * @since 1.0.0
 * @author Luis Raul Ariceaga, Santiago Cano Herrera
 */
import React, { useEffect, useState } from 'react';
import IOSSwitch from './IOSSwitch';
import {
  FormControlLabel,
  FormGroup,
  TextField,
  Chip,
  Box,
  Container,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import variables from '../../scss/_variables.scss';
import WeekDaysSelector from '../Config/WeekDaysSelector';

/**
 * A custom input selector component that dynamically renders different input types
 * based on the provided option data.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.optionData - The data for the current option.
 * @param {string} props.optionData.value_type - The type of the value (e.g., 'boolean', 'text', 'emails').
 * @param {string} props.optionData.label - The label for the input.
 * @param {string} props.optionData.value - The current value of the input.
 * @param {Function} props.cb - The callback function to handle value changes.
 *
 * @returns {JSX.Element} The rendered component.
 */

const InputSelector = ({ optionData, cb }) => {
  const [currentValue, setCurrentValue] = useState(optionData);
  const [changed, setChanged] = useState(false);
  const [valuesArray, setValuesArray] = useState([]);

  useEffect(() => {
    // Parse the value to JSON if possible, otherwise handle it as a string
    try {
      const parsedValue = JSON.parse(currentValue.value);
      if (Array.isArray(parsedValue)) {
        setValuesArray(parsedValue);
      } else {
        setValuesArray([]);
      }
    } catch (error) {
      setValuesArray([]);
    }
  }, [currentValue.value]);

  const [tempText, setTempText] = useState('');

  /**
   * Handles the deletion of a value from the values array.
   *
   * @param {number} indexToDelete - The index of the value to delete.
   */
  const handleDelete = (indexToDelete) => {
    setValuesArray((prevArray) => {
      const newArray = prevArray.filter((_, i) => i !== indexToDelete);
      setCurrentValue({ ...currentValue, value: JSON.stringify(newArray) });
      setChanged(true);
      return newArray;
    });
  };

  useEffect(() => {
    if (changed) cb(currentValue);
    return () => setChanged(false);
  }, [currentValue, changed]);

  switch (currentValue.value_type) {
    case 'boolean':
      const parsedValue = parseInt(currentValue.value);
      return (
        <FormGroup>
          <FormControlLabel
            label={parsedValue === 1 ? 'Activo' : 'Inactivo'}
            control={
              <IOSSwitch
                key={optionData.id}
                checked={parsedValue}
                onChange={(e) => {
                  setCurrentValue((prev) => ({
                    ...prev,
                    value: e.target.checked === true ? 1 : 0,
                  }));
                  setChanged(true);
                }}
                sx={{ m: 1 }}
              />
            }
            labelPlacement='start'
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          />
        </FormGroup>
      );

    case 'text':
      return (
        <TextField
          label={currentValue.label}
          key={optionData.id}
          type='text'
          size='small'
          value={currentValue.value || ''}
          onChange={(e) =>
            setCurrentValue((prev) => ({ ...prev, value: e.target.value }))
          }
          onBlur={(e) => setChanged(true)}
        />
      );

    case 'emails':
      const handleSubmit = (e) => {
        e.preventDefault();
        setValuesArray((prevArray) => {
          const newArray = [...prevArray, tempText];
          setCurrentValue({ ...currentValue, value: JSON.stringify(newArray) });
          setChanged(true);
          return newArray;
        });
        setTempText('');
      };

      return (
        <Container sx={{ display: 'flex', alignItems: 'center', width: '95%' }}>
          <form onSubmit={handleSubmit}>
            <TextField
              label={currentValue.label}
              key={currentValue.label}
              type='email'
              size='small'
              value={tempText || ''}
              onChange={(e) => setTempText(e.target.value)}
              sx={{ width: '200px', marginRight: 2 }}
            />
          </form>
          <Box>
            {valuesArray.map((value, index) => (
              <Chip
                onDelete={() => handleDelete(index)}
                key={index}
                label={value}
                sx={{ margin: 1 }}
              />
            ))}
          </Box>
        </Container>
      );

    case 'week':
      const weekObject = valuesArray;

      const handleToggle = (value) => {
        setValuesArray((prev) => {
          const newArray = prev.map((button) =>
            button.value === value
              ? { ...button, is_active: !button.is_active }
              : button
          );
          setCurrentValue((prev) => ({
            ...prev,
            value: JSON.stringify(newArray),
          }));
          setChanged(true);
          return newArray;
        });
      };

      return (
        <ToggleButtonGroup sx={{ marginLeft: 'auto' }}>
          {weekObject.map((button) => (
            <ToggleButton
              key={button.value}
              value={button.value}
              selected={button.is_active}
              onClick={(e, value) => handleToggle(button.value)}
              sx={{
                color: button.is_active ? 'white' : 'black',
                backgroundColor: button.is_active ? variables.redDabi : 'white',
                '&:hover': {
                  backgroundColor: button.is_active
                    ? variables.redDabi
                    : 'lightgray',
                },
              }}
            >
              {button.tag}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      );

    default:
      return <></>;
  }
};

export default InputSelector;
